/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {ColumnState} from 'ag-grid-community';
import produce from 'immer';
import {
  getGridColumnState,
  onSetGridColumns,
  onUpdateGridColumns
} from '@ion/applications/src/store/helpers';
import {PageState} from '@ion/applications/src/types/pages';
import {gridDefaultPageSize} from '@ion/applications/src/utils/helpers';
import {
  Device,
  DownloadJob,
  DownloadJobs,
  DownloadJobsFilters
} from '@ion/core/src/types/entities';
import {ServiceError} from '@ion/core/src/types/services';
import {convertModularSearchDateRange} from '@ion/core/src/utils/DateUtil';
import {AuditRecordType} from '@ion/dls/src/editors/audit/AuditGridUtils';
import {AsyncState} from '@/types/redux';
import {
  AUDIT_DOWNLOAD_JOBS,
  AUDIT_DOWNLOAD_JOBS_SHOW_CHANGES_DIALOG,
  CANCEL_DEVICE,
  CANCEL_DEVICE_ERROR,
  CANCEL_DEVICE_REQUEST,
  CANCEL_DEVICE_RESPONSE,
  CANCEL_JOB,
  CANCEL_JOB_ERROR,
  CANCEL_JOB_REQUEST,
  CANCEL_JOB_RESPONSE,
  CANCEL_TASK,
  CANCEL_TASK_ERROR,
  CANCEL_TASK_REQUEST,
  CANCEL_TASK_RESPONSE,
  DISPLAY_RERUN_JOB_DIALOG,
  DOWNLOAD_JOBS,
  DOWNLOAD_JOBS_GRID_REFRESHED,
  GET_DEVICES_STATUS_ERROR,
  GET_DOWNLOAD_DEVICES_JOBS_ERROR,
  GET_DOWNLOAD_JOBS_ERROR,
  GET_DOWNLOAD_JOBS_REQUEST,
  GET_DOWNLOAD_JOBS_RESPONSE,
  INITIALIZE_DOWNLOAD_JOBS,
  RERUN_JOB_ERROR,
  RERUN_JOB_REQUEST,
  RERUN_JOB_RESPONSE,
  RESET_DOWNLOAD_JOBS_SEARCH_QUERIES,
  SET_DEVICE_STATUS_RESPONSE,
  SET_GRID_COLUMNS,
  SET_GRID_PAGE_SIZE,
  SET_VIEW_SUB_GROUPS,
  SHOW_DEVICES_STATUS,
  SHOW_DOWNLOAD_JOBS,
  UPDATE_DOWNLOAD_JOBS_SEARCH_QUERIES,
  UPDATE_GRID_COLUMNS
} from '../actions/downloadjobs';

interface getDeviceStatus {
  items: Device | Device[] | null
}

export interface DownloadJobsAction {
  type: string;
  result?: any;
  error?: ServiceError;
  show?: boolean;
};

export interface DownloadJobAuditState extends DownloadJobActionState {
  showChangesDialog?: boolean;
  auditRecord?: AuditRecordType;
};

export interface DownloadJobAction extends DownloadJobsAction {
  downloadJob?: DownloadJobs;
  downloadJobs?: DownloadJobs[];
  searchParams?: DownloadJobsFilters;
};

interface DownloadJobActionState extends AsyncState {
  selected?: DownloadJobs | DownloadJobs[] | null;
  result?: DownloadJobs | DownloadJobs[] | null;
  deviceSelected?: Device | Device[] | null;
  deviceResult?: Device | Device[] | null;
  isEditing: boolean;
  loading: boolean;
  error: ServiceError;
  index?: number;
  isSpinner?: boolean;
};

export interface DownloadJobsState extends PageState {
  downloadjobs: {
    error: ServiceError;
    loading: boolean;
  },
  showDownloadJobs: boolean,
  showDevicesStatus: boolean,
  showExportJobs: boolean,
  downloadJobs: DownloadJob,
  exportJobs: DownloadJob,
  grid: {
    columnState: ColumnState[],
    preferedColumnState: ColumnState[],
    hasUpdate: boolean,
    includeSubGroups: boolean,
    pageSize: number
  },
  search: DownloadJobsFilters,
  getDeviceStatus: getDeviceStatus,
  cancel: DownloadJobActionState,
  cancelTask: DownloadJobActionState,
  cancelDevice: DownloadJobActionState,
  rerun: DownloadJobActionState,
  audit: DownloadJobAuditState,
};

// defines the initial state for the reducer ...
export const initialState: DownloadJobsState = {
  isInitialized : false,
  downloadjobs  : {
    error   : null,
    loading : false
  },
  showDownloadJobs  : false,
  showDevicesStatus : false,
  showExportJobs    : false,
  downloadJobs      : null,
  exportJobs        : null,
  grid              : {
    columnState         : null,
    preferedColumnState : null,
    hasUpdate           : false,
    includeSubGroups    : false,
    pageSize            : gridDefaultPageSize
  },
  search : {
    id        : null,
    createdOn : null
  },
  getDeviceStatus : {
    items : null
  },
  cancel : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  },
  cancelTask : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null,
    index     : null,
    isSpinner : false
  },
  cancelDevice : {
    isEditing      : false,
    deviceSelected : null,
    loading        : false,
    error          : null,
    deviceResult   : null,
    isSpinner      : false
  },
  audit : {
    isEditing         : false,
    selected          : null,
    loading           : false,
    error             : null,
    result            : null,
    showChangesDialog : false,
    auditRecord       : null
  },
  rerun : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  }
};

// defines the preference key for the provisioning download jobs page ...
export const PREFERENCE = 'schedule-downloadjobs';

// defines this reducers reducer functions ...
const reducers = {
  [INITIALIZE_DOWNLOAD_JOBS] : draft => {
    draft.isInitialized            = true;
    const preference               = getGridColumnState(PREFERENCE, true);
    draft.grid.columnState         = preference;
    draft.grid.preferedColumnState = preference;
  },
  [SET_GRID_COLUMNS] : ({grid, downloadjobs}: DownloadJobsState, {columnState}) => {
    onSetGridColumns({
      grid,
      isLoading  : downloadjobs.loading,
      preference : PREFERENCE
    }, columnState);
  },
  [UPDATE_GRID_COLUMNS] : ({grid}: DownloadJobsState, {columnState}) => {
    onUpdateGridColumns({grid, preference: PREFERENCE}, columnState);
  },
  [DOWNLOAD_JOBS_GRID_REFRESHED] : ({rerun}: DownloadJobsState) => {
    rerun.result = null;
  },
  [GET_DOWNLOAD_JOBS_REQUEST] : ({downloadjobs}: DownloadJobsState) => {
    downloadjobs.loading = true;
  },
  [GET_DOWNLOAD_JOBS_RESPONSE] : ({downloadjobs}: DownloadJobsState) => {
    downloadjobs.loading = false;
  },
  [GET_DOWNLOAD_JOBS_ERROR] : ({downloadjobs}: DownloadJobsState, {error}: DownloadJobsAction) => {
    downloadjobs.error   = error;
    downloadjobs.loading = false;
  },
  [GET_DOWNLOAD_DEVICES_JOBS_ERROR] : ({downloadjobs}: DownloadJobsState, {error}: DownloadJobsAction) => {
    downloadjobs.error   = error;
    downloadjobs.loading = false;
  },
  [GET_DEVICES_STATUS_ERROR] : ({downloadjobs}: DownloadJobsState, {error}: DownloadJobsAction) => {
    downloadjobs.error   = error;
    downloadjobs.loading = false;
  },
  [UPDATE_DOWNLOAD_JOBS_SEARCH_QUERIES] : ({search}: DownloadJobsState, {searchParams}) => {
    search.name           = searchParams.name ?? null;
    search.progressStatus = searchParams.progressStatus ?? null;
    search.id             = searchParams.id ?? null;
    search.createdOn      = searchParams?.createdOn ? `(${convertModularSearchDateRange(searchParams?.createdOn, 'YYYY-MM-DD[T]HH[\\]:mm[\\]:ss.[000Z]')})` : null;
  },
  [RESET_DOWNLOAD_JOBS_SEARCH_QUERIES] : ({search}: DownloadJobsState) => {
    search.name           = null;
    search.progressStatus = null;
    search.id             = null;
    search.createdOn      = null;
  },
  [SHOW_DOWNLOAD_JOBS] : (draft: DownloadJobsState, {downloadjobs, show}) => {
    draft.showDownloadJobs = show;
    draft.downloadJobs     = downloadjobs;
  },
  [SHOW_DEVICES_STATUS] : (draft: DownloadJobsState, {downloadjobs, show}) => {
    draft.showDevicesStatus = show;
    draft.downloadJobs      = downloadjobs;
  },
  [SET_DEVICE_STATUS_RESPONSE] : ({getDeviceStatus}: DownloadJobsState, {items}) => {
    getDeviceStatus.items = items;
  },
  [CANCEL_JOB] : ({cancel}: DownloadJobsState, {downloadjobs, show}) => {
    cancel.selected  = show ? downloadjobs : null;
    cancel.isEditing = show;
    cancel.error     = cancel.result = null;
  },
  [CANCEL_JOB_REQUEST] : ({cancel}: DownloadJobsState) => {
    cancel.loading = false;
    cancel.error   = cancel.result = null;
  },
  [CANCEL_JOB_RESPONSE] : ({cancel}: DownloadJobsState, {downloadjobs}) => {
    cancel.loading   = false;
    cancel.isEditing = false;
    cancel.result    = downloadjobs;
  },
  [CANCEL_JOB_ERROR] : ({cancel}: DownloadJobsState, {error}: DownloadJobsAction) => {
    cancel.loading = false;
    cancel.error   = error;
  },
  [CANCEL_TASK] : ({cancelTask}: DownloadJobsState, {downloadjobs, index, show}) => {
    cancelTask.selected  = show ? downloadjobs : null;
    cancelTask.index     = show ? index : null;
    cancelTask.isEditing = show;
    cancelTask.isSpinner = false;
    cancelTask.error     = cancelTask.result = null;
    cancelTask.loading   = false;
  },
  [CANCEL_TASK_REQUEST] : ({cancelTask}: DownloadJobsState) => {
    cancelTask.loading   = false;
    cancelTask.isSpinner = true;
    cancelTask.error     = cancelTask.result = null;
  },
  [CANCEL_TASK_RESPONSE] : ({cancelTask}: DownloadJobsState, {downloadjobs}) => {
    cancelTask.loading   = true;
    cancelTask.isEditing = false;
    cancelTask.result    = downloadjobs;
  },
  [CANCEL_TASK_ERROR] : ({cancelTask}: DownloadJobsState, {error}: DownloadJobsAction) => {
    cancelTask.loading = false;
    cancelTask.error   = error;
  },
  [CANCEL_DEVICE] : ({cancelDevice}: DownloadJobsState, {downloadjobs, devicestatus, show}) => {
    cancelDevice.deviceSelected = show ? devicestatus : null;
    cancelDevice.selected       = show ? downloadjobs : null;
    cancelDevice.isEditing      = show;
    cancelDevice.isSpinner      = false;
    cancelDevice.error          = cancelDevice.deviceResult = null;
    cancelDevice.loading        = false;
  },
  [CANCEL_DEVICE_REQUEST] : ({cancelDevice}: DownloadJobsState) => {
    cancelDevice.loading   = false;
    cancelDevice.isSpinner = true;
    cancelDevice.error     = cancelDevice.deviceResult = null;
  },
  [CANCEL_DEVICE_RESPONSE] : ({cancelDevice}: DownloadJobsState, {downloadjobs}) => {
    cancelDevice.loading      = true;
    cancelDevice.isEditing    = false;
    cancelDevice.deviceResult = downloadjobs;
  },
  [CANCEL_DEVICE_ERROR] : ({cancelDevice}: DownloadJobsState, {error}: DownloadJobsAction) => {
    cancelDevice.loading = false;
    cancelDevice.error   = error;
  },
  [AUDIT_DOWNLOAD_JOBS] : ({audit}: DownloadJobsState, {downloadjobs, show}) => {
    audit.selected  = show ? downloadjobs : null;
    audit.isEditing = show;
  },
  [AUDIT_DOWNLOAD_JOBS_SHOW_CHANGES_DIALOG] : ({audit}: DownloadJobsState, {auditRecord, show}) => {
    audit.showChangesDialog = show;
    audit.auditRecord       = auditRecord;
  },
  [DISPLAY_RERUN_JOB_DIALOG] : ({rerun}: DownloadJobsState, {job, show}) => {
    rerun.selected  = show ? job : null;
    rerun.isEditing = show;
    rerun.error     = rerun.result = null;
  },
  [RERUN_JOB_REQUEST] : ({rerun}: DownloadJobsState) => {
    rerun.loading = true;
    rerun.error   = rerun.result = null;
  },
  [RERUN_JOB_RESPONSE] : ({rerun}: DownloadJobsState, {job}) => {
    rerun.loading = false;
    rerun.result  = job;
  },
  [RERUN_JOB_ERROR] : ({rerun}: DownloadJobsState, {error}: DownloadJobsAction) => {
    rerun.loading = false;
    rerun.error   = error;
  },
  [DOWNLOAD_JOBS] : (draft: DownloadJobsState, {downloadjobs, show}) => {
    draft.showExportJobs = show;
    draft.exportJobs     = downloadjobs;
  },
  [SET_VIEW_SUB_GROUPS] : ({grid}: DownloadJobsState, {viewSubGroups}) => {
    grid.includeSubGroups = viewSubGroups;
  },
  [SET_GRID_PAGE_SIZE] : ({grid}: DownloadJobsState, {pageSize}) => {
    grid.pageSize = pageSize;
  }
};

// defines all reducers for actions of interest to the this reducer ...
export default produce((draft: DownloadJobsState = initialState, action) => {
  reducers?.[action.type]?.(draft, action);
  return draft;
});
